import type { AmiExclusions } from '../lib/peakExclusions';
import type { TimeRange } from '../lib/ami/suspectPeaks';

export type ProjectStatus = 'draft' | 'in-progress' | 'completed' | 'archived';

/** What the evaluated service feeds. Drives traditional-path (NEC 220.83)
 * scope eligibility only — measured projects never require it. */
export type OccupancyType =
  | 'one-family'
  | 'dwelling-unit'
  | 'multifamily-building'
  | 'non-dwelling';

export interface Project {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  project_name: string;
  status: ProjectStatus;
  last_opened_at: string;

  customer_name: string;
  customer_address: string;
  customer_city: string;
  customer_state: string;
  customer_zip: string;
  customer_email: string;
  customer_phone: string;
  prepared_by: string;
  preparer_title: string;
  preparer_license: string;
  preparer_email: string;
  company_name: string;
  /** Street line. Projects created before the address split hold the whole flattened address here. */
  company_address: string;
  company_city: string;
  company_state: string;
  company_zip: string;
  ahj_jurisdiction: string;
  permit_number: string;
  report_date: string | null;
  methodology_version: string;

  /** Printed description, e.g. "120/240V single-phase". Set from the configuration picker. */
  service_voltage_text: string;
  /** Line-to-line volts. Set from the configuration picker. */
  service_voltage_numeric: number;
  /** 'single' | 'three'. Selects the capacity formula. */
  service_phase: string;
  service_rating_amps: number;
  /** Retired from the UI 2026-08-07; column retained for existing projects. */
  panel_bus_rating_amps: number | null;
  service_type: string;
  installation_status: string;

  utility_id: string | null;
  utility_account_number?: string;
  ami_data_provider: string;
  ami_measurement_period: string;
  ami_occupancy_status: string;
  ami_interval_minutes: number | null;
  ami_data_quality_notes: string;
  /** The preparer's own notes about the building. Never machine-written. */
  ami_site_notes: string;
  max_recorded_demand_kw: number | null;
  peak_timestamp: string | null;

  /** What the last merge rewrote. Disclosed in the report; see amiMerge.ts. */
  ami_merge_conflicts_resolved?: number | null;
  ami_merge_duplicates_removed?: number | null;
  ami_merge_strategy?: string | null;

  /** Contractor-approved exclusions of flagged readings, with the raw peak.
   * Null when nothing is excluded. See src/lib/peakExclusions.ts. */
  ami_exclusions?: AmiExclusions | null;
  /** Runs of readings the source file marked as utility-estimated, captured
   * at upload (ami/estimatedReadings.ts). Null when none. */
  ami_estimated_runs?: TimeRange[] | null;

  assumptions_notes: string;

  gross_peak_kw?: number | null;
  ami_generation_column_detected?: boolean;
  location_id?: string | null;
  customer_id?: string | null;

  // Traditional calculation (NEC 220.83/220.82) — engine landed ahead of the
  // UI. Optional until the phase-1 migration applies; they are deliberately
  // NOT in toSupabasePayload() yet — the payload keys ship in the same batch
  // that applies the migration, per MIGRATIONS.md ordering.
  // See docs/specs/traditional-calcs.md §8.
  report_method?: 'measured' | 'traditional' | null;
  /** NEC edition pack key ('2017' | '2020' | '2023' | '2026'). Null/absent = follow
   * the customer's state (defaultEditionForState); set = the preparer's
   * explicit choice, or the edition frozen when the project was completed. */
  code_edition?: string | null;
  /** Calculation rules revision (src/lib/calc/rulesVersion.ts). Null/absent =
   * follow the current rules; set = frozen at completion, so a filed report
   * keeps calculating the way it was filed. */
  calc_rules_version?: number | null;
  /** 2026 measured path: apply Table 120.83 percentages to new load. Default
   * true; the preparer switches it off where the AHJ requires 100%. */
  measured_new_load_factors?: boolean;
  /** Read-only here: written by the completion update, never by the payload. */
  completed_at?: string | null;
  /** 'auto' or a forced submethod ('220.83A' | '220.83B' | '220.82' | 'standard'). */
  traditional_submethod?: string;
  dwelling_floor_area_sqft?: number | null;
  small_appliance_circuit_count?: number;
  laundry_circuit_count?: number;
  /** How EVSE enters the 2020/2023 optional-method pool; 2026 sets it in code text. */
  evse_demand_policy?: 'pooled' | 'full';
  /** Preparer attests the fastened-in-place appliance inventory is complete. */
  inventory_attested?: boolean;
  /** NULL = unanswered. Traditional-path eligibility only (calc/eligibility.ts). */
  occupancy_type?: OccupancyType | null;
  /** Preparer election: 220.60 noncoincident treatment under 220.83(A), which
   * the section itself does not grant. Default false. (B) is unaffected. */
  noncoincident_election?: boolean;
  /** Preparer-stated demand parameters (2020/2023 AB packs only; null = the
   * section values). Printed on the report as a stated election. */
  override_tier_kva?: number | null;
  override_tier_percent?: number | null;
  override_remainder_percent?: number | null;
  /** EVSE percent when evse_demand_policy is 'full' (separate line). Null = 100. */
  override_evse_percent?: number | null;
  /** Square footage of an addition — its own general-lighting worksheet line. */
  added_floor_area_sqft?: number | null;
  /** Apply the 7,200 VA EVSE minimum imported from 220.57 inside the 220.83
   * pool — a conservative election, not a section requirement. Default true. */
  apply_evse_floor?: boolean;

  // Set by Netlify Functions via service role; never written by the client
  results_unlocked?: boolean;
  results_unlocked_at?: string | null;
  ami_replace_count?: number;
  /** Set by the nightly retention sweep when this project's raw AMI intervals
   * were deleted under its firm's policy. Non-null means the report can no
   * longer be regenerated with the load chart. Server-side only: deliberately
   * absent from toSupabasePayload(), because a client write must never touch
   * a retention marker. */
  intervals_purged_at?: string | null;
  /** Set by the nightly retention sweep when end-customer identity was cleared
   * from this project under its firm's policy. Server-side only, for the same
   * reason as above. */
  identity_redacted_at?: string | null;
}

export interface Equipment {
  id: string;
  project_id: string;
  equipment_name: string;
  equipment_type: EquipmentType | '';
  source?: 'Nameplate' | 'MCA';
  quantity: number;
  amps: number | null;
  kw: number | null;
  voltage: number;
  is_concurrent: boolean;
  concurrency_reason: string | null;
  sort_order: number;
  created_at: string;

  // Traditional calculation — optional until the phase-1 migration applies
  // (docs/specs/traditional-calcs.md §8).
  /** 'proposed' rows are the Equipment tab; 'existing' rows are the dwelling inventory. */
  /** 'removed' = taken out with this work (loadRoles.ts removedLoads). */
  load_role?: 'proposed' | 'existing' | 'removed';
  /** 'default' = untouched checklist seed value; flips to 'nameplate' on edit. Disclosed in the report. */
  value_source?: 'default' | 'nameplate';
  /** Dryer rows: true = on the laundry branch circuit already counted at
   * 1,500 VA (220.83 item (2)), so not separately counted under (3)(c).
   * Default false = dedicated circuit, counted at nameplate. */
  on_laundry_circuit?: boolean;
  /** Heat Pump rows: the entered rating includes backup strip heat. Null =
   * not answered. Under 2026 (rules revision 4+) true or null puts a new heat
   * pump in Table 120.83's central-resistance row (80%); false keeps it in
   * "all other new loads" (50%). See loadRoles.ts countsAsCentralResistanceHeat. */
  includes_backup_heat?: boolean | null;
}

export interface AMIDataPoint {
  id: string;
  project_id: string;
  timestamp: string;
  kw: number;
  created_at: string;
}

export type EquipmentType =
  | 'Heat Pump'
  | 'Strip Heat'
  | 'A/C'
  | 'EVSE'
  | 'Range'
  | 'Dryer'
  | 'Water Heater'
  | 'Pool Pump'
  | 'Pool Heater'
  | 'Other';

export const EQUIPMENT_TYPES: EquipmentType[] = [
  'Heat Pump',
  'Strip Heat',
  'A/C',
  'EVSE',
  'Range',
  'Dryer',
  'Water Heater',
  'Pool Pump',
  'Pool Heater',
  'Other',
];
